import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, MapPin, Loader2, CheckCircle2, PhoneCall } from "lucide-react";
import { submitContact } from "@/lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    organisation: "",
    service_interest: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Call back request form state
  const [cb, setCb] = useState({
    name: "",
    email: "",
    phone: "",
    best_time: "",
    notes: "",
  });
  const [cbLoading, setCbLoading] = useState(false);
  const [cbDone, setCbDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact(form);
      setDone(true);
      toast.success("Enquiry sent we'll be in touch within 1 business day.");
    } catch (err) {
      toast.error("Couldn't send enquiry", { description: err?.response?.data?.detail || "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const submitCallback = async (e) => {
    e.preventDefault();
    setCbLoading(true);
    try {
      await submitContact({
        name: cb.name,
        email: cb.email,
        phone: cb.phone,
        organisation: "",
        service_interest: "Call back request",
        message: `CALL BACK REQUEST\n\nBest time to call: ${cb.best_time || "Not specified"}\n\nNotes: ${cb.notes || "None"}`,
      });
      setCbDone(true);
      toast.success("Call back requested we'll ring you soon.");
    } catch (err) {
      toast.error("Couldn't submit request", { description: err?.response?.data?.detail || "Please try again." });
    } finally {
      setCbLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="container-pad pt-16 pb-12">
        <div className="overline">Get in touch</div>
        <h1 className="font-display text-5xl sm:text-6xl text-navy mt-6 tracking-tight max-w-4xl leading-[1.05]">
          Let's start with a free 30 minute call.
        </h1>
        <p className="text-muted2 mt-8 max-w-2xl leading-relaxed text-lg">
          Tell us about your provider goals and we'll map out the registration path together. No pressure, no obligation.
        </p>
      </section>

      <section className="container-pad pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* CONTACT INFO */}
          <div className="md:col-span-4">
            <div className="bg-brand-gradient text-canvas rounded-3xl p-10 h-full grain relative overflow-hidden">
              <div className="overline" style={{ color: '#A7E0E6' }}>Reach us directly</div>
              <ul className="mt-8 space-y-6 relative">
                <li className="flex items-start gap-3">
                  <Mail size={18} className="text-canvas mt-0.5" />
                  <div>
                    <div className="text-xs uppercase tracking-widest text-canvas/60">Email</div>
                    <a href="mailto:Jordan@elevateconsultingau.com" data-testid="contact-email" className="font-display text-lg hover:underline break-all">Jordan@elevateconsultingau.com</a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin size={18} className="text-canvas mt-0.5" />
                  <div>
                    <div className="text-xs uppercase tracking-widest text-canvas/60">Servicing</div>
                    <div className="font-display text-lg">Headquartered in NSW · Australia wide</div>
                  </div>
                </li>
              </ul>
              <div className="mt-10 pt-10 border-t border-canvas/15 relative">
                <div className="overline" style={{ color: '#A7E0E6' }}>Hours</div>
                <p className="text-canvas/80 text-sm mt-3 leading-relaxed">
                  Mon to Fri, 9am to 5pm AEST<br />
                  Discovery calls available outside hours by request.
                </p>
              </div>
            </div>
          </div>

          {/* FORM */}
          <div className="md:col-span-8">
            <div className="card-soft p-10">
              {done ? (
                <div data-testid="contact-success" className="text-center py-12">
                  <div className="w-16 h-16 mx-auto rounded-full bg-teal/10 flex items-center justify-center">
                    <CheckCircle2 size={36} className="text-teal" />
                  </div>
                  <h3 className="font-display text-3xl text-navy mt-6 tracking-tight">Message received</h3>
                  <p className="text-muted2 mt-4 max-w-md mx-auto">
                    Thanks a senior consultant will be in touch within one business day. Check your inbox for confirmation.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5">
                  <div className="overline">Enquiry form</div>
                  <h3 className="font-display text-2xl text-navy">Tell us about your provider</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="c-name" className="text-xs text-muted2">Full name *</Label>
                      <Input id="c-name" data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="c-email" className="text-xs text-muted2">Email *</Label>
                      <Input id="c-email" type="email" data-testid="contact-email-input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="c-phone" className="text-xs text-muted2">Phone</Label>
                      <Input id="c-phone" data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="c-org" className="text-xs text-muted2">Organisation</Label>
                      <Input id="c-org" data-testid="contact-org" value={form.organisation} onChange={(e) => setForm({ ...form, organisation: e.target.value })} className="mt-1" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted2">Service of interest</Label>
                    <Select value={form.service_interest} onValueChange={(v) => setForm({ ...form, service_interest: v })}>
                      <SelectTrigger data-testid="contact-service-select" className="mt-1">
                        <SelectValue placeholder="Select a service…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="end to end-registration">End to end NDIS registration</SelectItem>
                        <SelectItem value="compliance-audit">Compliance audit / internal review</SelectItem>
                        <SelectItem value="policies">Policies & procedures</SelectItem>
                        <SelectItem value="retainer">Ongoing retainer</SelectItem>
                        <SelectItem value="other">Something else</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="c-message" className="text-xs text-muted2">How can we help? *</Label>
                    <Textarea id="c-message" data-testid="contact-message" rows={5} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1" />
                  </div>
                  <button data-testid="contact-submit" disabled={loading} className="btn-ochre w-full justify-center disabled:opacity-60">
                    {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                    {loading ? "Sending…" : "Send enquiry"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CALL BACK REQUEST */}
      <section className="bg-sand py-24">
        <div className="container-pad">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-5">
              <div className="overline">Request a call back</div>
              <h2 className="font-display text-4xl sm:text-5xl text-navy mt-4 tracking-tight leading-[1.08]">
                Prefer we call <span className="text-brand-gradient font-serif-mark italic">you</span>?
              </h2>
              <p className="text-muted2 mt-6 leading-relaxed">
                Leave your details and a preferred time. A senior consultant will call you back for a free 30 minute
                discovery conversation.
              </p>
              <div className="mt-8 flex items-center gap-3 text-sm text-ink">
                <div className="w-10 h-10 rounded-full bg-teal/10 flex items-center justify-center">
                  <PhoneCall size={18} className="text-teal" />
                </div>
                <div>
                  <div className="font-medium">No pressure. No obligation.</div>
                  <div className="text-muted2 text-xs mt-0.5">We call back within one business day.</div>
                </div>
              </div>
            </div>

            <div className="md:col-span-7">
              <div className="card-soft p-8 md:p-10">
                {cbDone ? (
                  <div data-testid="callback-success" className="text-center py-10">
                    <div className="w-16 h-16 mx-auto rounded-full bg-teal/10 flex items-center justify-center">
                      <CheckCircle2 size={36} className="text-teal" />
                    </div>
                    <h3 className="font-display text-2xl text-navy mt-5 tracking-tight">Call back requested</h3>
                    <p className="text-muted2 mt-3 max-w-md mx-auto text-sm">
                      We've got your details. A senior consultant will ring you back within one business day.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={submitCallback} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="cb-name" className="text-xs text-muted2">Full name *</Label>
                        <Input id="cb-name" data-testid="callback-name" required value={cb.name} onChange={(e) => setCb({ ...cb, name: e.target.value })} className="mt-1" />
                      </div>
                      <div>
                        <Label htmlFor="cb-phone" className="text-xs text-muted2">Phone number *</Label>
                        <Input id="cb-phone" type="tel" data-testid="callback-phone" required value={cb.phone} onChange={(e) => setCb({ ...cb, phone: e.target.value })} className="mt-1" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="cb-email" className="text-xs text-muted2">Email *</Label>
                      <Input id="cb-email" type="email" data-testid="callback-email" required value={cb.email} onChange={(e) => setCb({ ...cb, email: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-muted2">Best time to call *</Label>
                      <Select value={cb.best_time} onValueChange={(v) => setCb({ ...cb, best_time: v })} required>
                        <SelectTrigger data-testid="callback-time-select" className="mt-1">
                          <SelectValue placeholder="Choose a time window" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Morning (9am to 12pm AEST)">Morning (9am to 12pm AEST)</SelectItem>
                          <SelectItem value="Early afternoon (12pm to 3pm AEST)">Early afternoon (12pm to 3pm AEST)</SelectItem>
                          <SelectItem value="Late afternoon (3pm to 5pm AEST)">Late afternoon (3pm to 5pm AEST)</SelectItem>
                          <SelectItem value="Anytime during business hours">Anytime during business hours</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="cb-notes" className="text-xs text-muted2">Anything we should know? (optional)</Label>
                      <Textarea id="cb-notes" data-testid="callback-notes" rows={3} value={cb.notes} onChange={(e) => setCb({ ...cb, notes: e.target.value })} className="mt-1" placeholder="Registration groups, current stage, timeline..." />
                    </div>
                    <button data-testid="callback-submit" disabled={cbLoading} className="btn-ochre w-full justify-center disabled:opacity-60">
                      {cbLoading ? <Loader2 size={16} className="animate-spin" /> : <PhoneCall size={16} />}
                      {cbLoading ? "Submitting..." : "Request my call back"}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
